import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Briefcase, Plus, Clock, Building2, Settings2, Trash2, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import {
  useMentorshipProjects, useMentorshipTypes, useSaveMentorshipProject, useMentorCandidates, projectSupportMentorIds,
  useOrganizationsList, useSaveMentorshipType, useDeleteMentorshipType, type MentorshipProject,
} from '@/hooks/useCustomMentorships';

const emptyForm = {
  organization_id: '',
  name: '',
  scope_description: '',
  type_id: '',
  contracted_hours: '0',
  start_date: '',
  end_date: '',
  lead_mentor_id: '',
  contract_reference: '',
  supportMentorIds: [] as string[],
};

export default function CustomMentorshipsPage() {
  const navigate = useNavigate();
  const { roles } = useAuth();
  const isSuperAdmin = roles.includes('super_admin');

  const { data: projects = [], isLoading } = useMentorshipProjects();
  const { data: types = [] } = useMentorshipTypes();
  const { data: organizations = [] } = useOrganizationsList();
  const { data: mentors = [] } = useMentorCandidates(isSuperAdmin);
  const saveProject = useSaveMentorshipProject();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<MentorshipProject | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [typesOpen, setTypesOpen] = useState(false);

  const [search, setSearch] = useState('');
  const [orgFilter, setOrgFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return projects.filter((p) => {
      if (orgFilter !== 'all' && p.organization_id !== orgFilter) return false;
      if (typeFilter !== 'all' && p.type_id !== typeFilter) return false;
      if (statusFilter !== 'all' && p.status !== statusFilter) return false;
      if (term && !`${p.name} ${p.organizations?.name ?? ''}`.toLowerCase().includes(term)) return false;
      return true;
    });
  }, [projects, search, orgFilter, typeFilter, statusFilter]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (p: MentorshipProject) => {
    setEditing(p);
    setForm({
      organization_id: p.organization_id,
      name: p.name,
      scope_description: p.scope_description ?? '',
      type_id: p.type_id ?? '',
      contracted_hours: String(p.contracted_hours ?? 0),
      start_date: p.start_date ?? '',
      end_date: p.end_date ?? '',
      lead_mentor_id: p.lead_mentor_id ?? '',
      contract_reference: p.contract_reference ?? '',
      supportMentorIds: projectSupportMentorIds(p),
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.organization_id || !form.name.trim()) {
      toast.error('La empresa y el nombre son obligatorios');
      return;
    }
    try {
      await saveProject.mutateAsync({
        id: editing?.id,
        organization_id: form.organization_id,
        name: form.name.trim(),
        scope_description: form.scope_description,
        type_id: form.type_id || null,
        contracted_hours: Number(form.contracted_hours || 0),
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        lead_mentor_id: form.lead_mentor_id || null,
        contract_reference: form.contract_reference,
        supportMentorIds: form.supportMentorIds,
      } as any);
      toast.success(editing ? 'Proyecto actualizado' : 'Proyecto creado');
      setDialogOpen(false);
    } catch (e: any) {
      toast.error(e?.message ?? 'No se pudo guardar el proyecto');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Briefcase className="w-6 h-6 text-primary" />
            Mentorías Personalizadas
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Bolsas de horas de mentoría contratadas por empresas, independientes de las rutas.
          </p>
        </div>
        {isSuperAdmin && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setTypesOpen(true)}>
              <Settings2 className="w-4 h-4 mr-2" /> Tipos
            </Button>
            <Button onClick={openCreate}>
              <Plus className="w-4 h-4 mr-2" /> Nuevo proyecto
            </Button>
          </div>
        )}
      </div>

      <Card>
        <CardContent className="pt-6 grid gap-3 md:grid-cols-4">
          <Input placeholder="Buscar por nombre o empresa…" value={search} onChange={(e) => setSearch(e.target.value)} />
          <Select value={orgFilter} onValueChange={setOrgFilter}>
            <SelectTrigger><SelectValue placeholder="Empresa" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las empresas</SelectItem>
              {organizations.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger><SelectValue placeholder="Tipo" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los tipos</SelectItem>
              {types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger><SelectValue placeholder="Estado" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              <SelectItem value="active">Activos</SelectItem>
              <SelectItem value="closed">Cerrados</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-40 w-full" />)}
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            {projects.length === 0
              ? 'Todavía no hay proyectos de mentoría personalizada.'
              : 'Ningún proyecto coincide con los filtros.'}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {filtered.map((p) => (
            <ProjectCard key={p.id} project={p} onOpen={() => navigate(`/mentorias/${p.id}`)} onEdit={isSuperAdmin ? () => openEdit(p) : undefined} />
          ))}
        </div>
      )}

      {/* Dialogo crear/editar proyecto */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar proyecto' : 'Nuevo proyecto de mentoría'}</DialogTitle>
            <DialogDescription>Solo el super admin crea proyectos y fija las horas contratadas.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Empresa cliente *</Label>
              <Select value={form.organization_id} onValueChange={(v) => setForm({ ...form, organization_id: v })}>
                <SelectTrigger><SelectValue placeholder="Selecciona" /></SelectTrigger>
                <SelectContent>
                  {organizations.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Descripción del alcance</Label>
              <Textarea value={form.scope_description} onChange={(e) => setForm({ ...form, scope_description: e.target.value })} rows={3} />
            </div>
            <div className="space-y-2">
              <Label>Tipo predominante</Label>
              <Select value={form.type_id} onValueChange={(v) => setForm({ ...form, type_id: v })}>
                <SelectTrigger><SelectValue placeholder="Selecciona" /></SelectTrigger>
                <SelectContent>
                  {types.filter((t) => t.is_active).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Horas contratadas</Label>
              <Input type="number" min={0} step="0.5" value={form.contracted_hours} onChange={(e) => setForm({ ...form, contracted_hours: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Fecha de inicio</Label>
              <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Fecha de fin</Label>
              <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Mentor líder</Label>
              <Select value={form.lead_mentor_id} onValueChange={(v) => setForm({ ...form, lead_mentor_id: v })}>
                <SelectTrigger><SelectValue placeholder="Selecciona" /></SelectTrigger>
                <SelectContent>
                  {mentors.map((m) => <SelectItem key={m.id} value={m.id}>{m.full_name || m.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Referencia del contrato</Label>
              <Input value={form.contract_reference} onChange={(e) => setForm({ ...form, contract_reference: e.target.value })} />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Mentores de apoyo de la mentoría</Label>
              <p className="text-xs text-muted-foreground">
                Acompañan al mentor líder en toda la mentoría. El apoyo de cada grupo se elige entre ellos y el líder; si
                uno sale de la mentoría, sus grupos quedan sin apoyo y los atiende el líder.
              </p>
              <div className="max-h-40 overflow-y-auto rounded-md border p-3 space-y-2">
                {mentors.filter((m) => m.id !== form.lead_mentor_id).map((m) => (
                  <label key={m.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={form.supportMentorIds.includes(m.id)}
                      onCheckedChange={(c) =>
                        setForm({
                          ...form,
                          supportMentorIds: c
                            ? [...form.supportMentorIds, m.id]
                            : form.supportMentorIds.filter((id) => id !== m.id),
                        })
                      }
                    />
                    {m.full_name || m.email}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saveProject.isPending}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TypesDialog open={typesOpen} onOpenChange={setTypesOpen} />
    </div>
  );
}

function ProjectCard({ project, onOpen, onEdit }: { project: MentorshipProject; onOpen: () => void; onEdit?: () => void }) {
  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-lg">{project.name}</CardTitle>
            <CardDescription className="flex items-center gap-1 mt-1">
              <Building2 className="w-3.5 h-3.5" />
              {project.organizations?.name ?? 'Sin empresa'}
            </CardDescription>
          </div>
          <div className="flex flex-col items-end gap-1">
            <Badge variant={project.status === 'closed' ? 'secondary' : 'default'}>
              {project.status === 'closed' ? 'Cerrado' : 'Activo'}
            </Badge>
            {project.mentorship_project_types?.name && (
              <Badge variant="outline">{project.mentorship_project_types.name}</Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Clock className="w-4 h-4" />
          {Number(project.contracted_hours)} horas contratadas
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={onOpen}>
            Abrir <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
          {onEdit && <Button size="sm" variant="outline" onClick={onEdit}>Editar</Button>}
        </div>
      </CardContent>
    </Card>
  );
}

function TypesDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const { data: types = [] } = useMentorshipTypes();
  const saveType = useSaveMentorshipType();
  const deleteType = useDeleteMentorshipType();
  const [name, setName] = useState('');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Tipos de mentoría</DialogTitle>
          <DialogDescription>Catálogo usado al crear proyectos.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {types.map((t) => (
            <div key={t.id} className="flex items-center justify-between gap-2 rounded-md border p-2">
              <span className="text-sm">{t.name}</span>
              <Button
                size="icon"
                variant="ghost"
                onClick={async () => {
                  try {
                    await deleteType.mutateAsync(t.id);
                    toast.success('Tipo eliminado');
                  } catch {
                    toast.error('No se pudo eliminar: hay proyectos que lo usan');
                  }
                }}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <div className="flex gap-2">
            <Input placeholder="Nuevo tipo" value={name} onChange={(e) => setName(e.target.value)} />
            <Button
              onClick={async () => {
                if (!name.trim()) return;
                await saveType.mutateAsync({ name: name.trim(), sort_order: types.length + 1 });
                setName('');
                toast.success('Tipo agregado');
              }}
            >
              Agregar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
